import { useState, useEffect } from 'react';
import { User } from '../types';
import { decodeJWT } from '../utils/jwt';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

export const useAuth = (): AuthContextType => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for existing session
    const checkAuth = async () => {
      try {
        const accessToken = localStorage.getItem('accessToken');
        if (!accessToken) {
          console.log('ℹ️ No accessToken found in localStorage');
          setIsLoading(false);
          return;
        }

        // JWT is the source of truth - decode it first
        let decoded;
        try {
          decoded = decodeJWT(accessToken);
          if (!decoded) {
            throw new Error('Failed to decode JWT: decoded value is null');
          }
        } catch (decodeError) {
          console.error('❌ Failed to decode JWT token - possible tampering or invalid token:', decodeError);
          // If JWT decoding fails, don't trust localStorage - clear everything
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('userEmail');
          localStorage.removeItem('adminId');
          setUser(null);
          setIsLoading(false);
          return;
        }

        // Validate required fields in decoded token
        if (!decoded.email && !decoded.userId) {
          console.error('❌ Invalid JWT: missing required fields (email or userId)');
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('userEmail');
          localStorage.removeItem('adminId');
          setUser(null);
          setIsLoading(false);
          return;
        }

        // Use decoded JWT as primary source, localStorage as fallback for fields not in JWT
        const email = decoded.email || localStorage.getItem('userEmail') || '';
        const adminId = decoded.userId || decoded.adminId || localStorage.getItem('adminId') || '';
        const role = (decoded.role || localStorage.getItem('userRole') || 'admin') as 'admin' | 'super_admin' | 'moderator';
        
        // Store role in localStorage if it's in the JWT but not in localStorage
        if (decoded.role && decoded.role !== localStorage.getItem('userRole')) {
          localStorage.setItem('userRole', decoded.role);
          console.log('✅ Stored role from JWT to localStorage:', decoded.role);
        }
        
        // Create user object from validated JWT data
        const userData: User = {
          id: adminId,
          email: email,
          name: decoded.name || email.split('@')[0] || 'Admin',
          role: role,
          avatar: decoded.avatar,
          isActive: true,
          createdAt: new Date(decoded.iat ? decoded.iat * 1000 : Date.now()).toISOString(),
          lastLogin: new Date().toISOString(),
        };
        
        console.log('✅ Auth check successful, user authenticated:', { id: userData.id, email: userData.email });
        setUser(userData);
      } catch (error) {
        console.error('❌ Auth check failed with unexpected error:', error);
        // On unexpected errors, clear tokens for security
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('userEmail');
        localStorage.removeItem('adminId');
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    setIsLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock authentication - in real app, call API
      if (email === 'admin@eventi.com' && password === 'admin123') {
        const mockUser: User = {
          id: '1',
          email: email,
          name: 'Admin User',
          role: 'super_admin',
          avatar: 'https://via.placeholder.com/150',
          isActive: true,
          createdAt: new Date().toISOString(),
          lastLogin: new Date().toISOString(),
        };
        
        localStorage.setItem('authToken', 'mock-jwt-token');
        setUser(mockUser);
      } else {
        throw new Error('Invalid credentials');
      }
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    // Clear all authentication data
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('adminId');
    localStorage.removeItem('userRole');
    setUser(null);
  };

  return {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
  };
};
